import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowRight, faArrowUpRightFromSquare, faBoxesStacked, faHeadSideMask, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons'
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons'

import type { MedicalMaskDefinition, MedicalSupplyDefinition } from '../data/medicalSupplies'
import { whatsAppCtaProps, trackSelectItem } from '../lib/analytics'
import { WA_NUMBER } from './SiteChrome'

export type MedicalSupplyCard = MedicalSupplyDefinition
export type MedicalMaskCard = MedicalMaskDefinition

function waLink(message: string) {
  return `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(message)}`
}

export function MedicalWhatsAppLink({
  label,
  placement,
  product,
  kind = 'glove',
  className,
}: {
  label: string
  placement: string
  product?: Pick<MedicalSupplyCard, 'name' | 'sku'>
  kind?: 'glove' | 'mask'
  className: string
}) {
  const message = product
    ? kind === 'mask'
      ? `Halo Dropify, saya mau cek ${product.name}. Kebutuhan: ___ box, warna: ___, kirim ke ___. Mohon info stok, quotation, dan dokumen produk yang tersedia.`
      : `Halo Dropify, saya mau cek ${product.name}. Kebutuhan: ___ box, ukuran dan jumlah per ukuran: ___, kirim ke ___. Mohon info stok, quotation, dan dokumen produk yang tersedia.`
    : 'Halo Dropify, saya mencari perlengkapan medis grosir. Produk (sarung tangan / masker): ___. Kebutuhan: ___ box, kirim ke ___. Mohon katalog, quotation, dan dokumen produk yang tersedia.'

  return (
    <a
      href={waLink(message)}
      target="_blank"
      rel="noopener noreferrer"
      {...whatsAppCtaProps('landing_page', {
          cta_placement: placement,
          lp_category: 'Perlengkapan Medis',
          ...(product
            ? {
                lead_item_name: product.name,
                items: [{ item_id: product.sku, item_name: product.name, item_category: 'Perlengkapan Medis' }],
              }
            : {}),
        })}
      className={className}
    >
      <FontAwesomeIcon icon={faWhatsapp} className="h-5 w-5 flex-shrink-0" />
      {label}
    </a>
  )
}

function ProductCard({ product, index }: { product: MedicalSupplyCard; index: number }) {
  const href = `/produk/${product.slug}`
  const selectProduct = () =>
    trackSelectItem(
      {
        item_id: product.sku,
        item_name: product.name,
        item_category: 'Perlengkapan Medis',
        index,
      },
      'perlengkapan_medis_catalog',
    )

  return (
    <article className="group flex h-full flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition-all hover:-translate-y-0.5 hover:border-sky-300 hover:shadow-xl">
      <a href={href} onClick={selectProduct} className="relative block overflow-hidden bg-slate-50 p-4">
        <img
          src={product.image}
          alt={`${product.name} kemasan box`}
          width="520"
          height="620"
          loading="lazy"
          decoding="async"
          className="aspect-[4/5] w-full object-contain transition-transform duration-300 group-hover:scale-[1.025]"
        />
        <span className="absolute left-4 top-4 rounded-full bg-slate-950/90 px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-white">
          {product.packSize} pcs / box
        </span>
      </a>
      <div className="flex flex-1 flex-col p-5">
        <div className="flex flex-wrap gap-2 text-xs font-bold">
          <span className="rounded-full bg-sky-50 px-2.5 py-1 text-sky-800">{product.material}</span>
          <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-emerald-800">{product.powder}</span>
          {product.color && <span className="rounded-full bg-slate-100 px-2.5 py-1 text-slate-700">{product.color}</span>}
        </div>
        <a href={href} onClick={selectProduct} className="mt-4 inline-flex flex-1 items-start justify-between gap-3 text-slate-950 hover:text-sky-800">
          <h3 className="text-lg font-black leading-snug tracking-tight">{product.name}</h3>
          <FontAwesomeIcon icon={faArrowRight} className="mt-1 h-4 w-4 flex-shrink-0" />
        </a>
        <MedicalWhatsAppLink
          product={product}
          label="Tanyakan kepada admin"
          placement={`product_card_${index + 1}`}
          className="mt-5 inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-green-700 px-4 py-3 text-sm font-bold text-white transition-colors hover:bg-green-800"
        />
      </div>
    </article>
  )
}

export function MedicalProductCatalog({ products }: { products: MedicalSupplyCard[] }) {
  return (
    <section id="produk-medis" className="scroll-mt-6 bg-slate-50 py-16 md:py-20">
      <div className="mx-auto max-w-7xl px-4">
        <div className="flex flex-col justify-between gap-5 md:flex-row md:items-end">
          <div className="max-w-3xl">
            <p className="inline-flex items-center gap-2 text-xs font-black uppercase tracking-[0.2em] text-sky-800">
              <FontAwesomeIcon icon={faBoxesStacked} className="h-4 w-4" />
              Produk yang kami jual
            </p>
            <h2 className="mt-3 text-3xl font-black tracking-tight text-slate-950 md:text-4xl">{products.length} pilihan sarung tangan medis</h2>
            <p className="mt-4 text-base leading-relaxed text-slate-600">
              Kirim nama produk dan jumlah saat menghubungi admin. Admin akan memeriksa stok serta ukuran yang tersedia untuk produk tersebut.
            </p>
          </div>
          <p className="text-sm font-semibold text-slate-500">Isi box 50 atau 100 pcs sesuai produk</p>
        </div>
        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {products.map((product, index) => <ProductCard key={product.sku} product={product} index={index} />)}
        </div>
        <div className="mt-6 flex items-start gap-3 rounded-2xl border border-slate-200 bg-white p-5 text-sm leading-relaxed text-slate-600">
          <FontAwesomeIcon icon={faTriangleExclamation} className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600" />
          <p><strong className="text-slate-900">Stok per ukuran berubah mengikuti pesanan yang masuk.</strong> Sebelum quotation disetujui, admin mengirim foto barang aktual sebagai dasar konfirmasi ukuran dan kemasan.</p>
        </div>
      </div>
    </section>
  )
}

function MaskCard({ mask, index }: { mask: MedicalMaskCard; index: number }) {
  const href = `/produk/${mask.slug}`
  const selectMask = () =>
    trackSelectItem(
      {
        item_id: mask.sku,
        item_name: mask.name,
        item_category: 'Perlengkapan Medis',
        index,
      },
      'perlengkapan_medis_masker',
    )

  return (
    <article className="group flex h-full flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition-all hover:-translate-y-0.5 hover:border-sky-300 hover:shadow-xl">
      <a href={href} onClick={selectMask} className="relative block overflow-hidden bg-slate-50 p-4">
        <img
          src={mask.image}
          alt={`${mask.name} kemasan box`}
          width="520"
          height="520"
          loading="lazy"
          decoding="async"
          className="aspect-square w-full object-contain transition-transform duration-300 group-hover:scale-[1.025]"
        />
        <span className="absolute left-4 top-4 rounded-full bg-slate-950/90 px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-white">
          {mask.packSize} pcs / box
        </span>
      </a>
      <div className="flex flex-1 flex-col p-5">
        <span className="self-start rounded-full bg-sky-50 px-2.5 py-1 text-xs font-bold text-sky-800">{mask.model}</span>
        <a href={href} onClick={selectMask} className="mt-4 inline-flex items-start justify-between gap-3 text-slate-950 hover:text-sky-800">
          <h3 className="text-lg font-black leading-snug tracking-tight">{mask.name}</h3>
          <FontAwesomeIcon icon={faArrowRight} className="mt-1 h-4 w-4 flex-shrink-0" />
        </a>
        <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-600">{mask.note}</p>
        <MedicalWhatsAppLink
          product={mask}
          kind="mask"
          label="Tanyakan kepada admin"
          placement={`mask_card_${index + 1}`}
          className="mt-5 inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-green-700 px-4 py-3 text-sm font-bold text-white transition-colors hover:bg-green-800"
        />
      </div>
    </article>
  )
}

export function MedicalMaskCatalog({ masks }: { masks: MedicalMaskCard[] }) {
  return (
    <section id="produk-masker" className="scroll-mt-6 bg-white py-16 md:py-20">
      <div className="mx-auto max-w-7xl px-4">
        <div className="flex flex-col justify-between gap-5 md:flex-row md:items-end">
          <div className="max-w-3xl">
            <p className="inline-flex items-center gap-2 text-xs font-black uppercase tracking-[0.2em] text-sky-800">
              <FontAwesomeIcon icon={faHeadSideMask} className="h-4 w-4" />
              Masker
            </p>
            <h2 className="mt-3 text-3xl font-black tracking-tight text-slate-950 md:text-4xl">{masks.length} tipe masker COMET Theo</h2>
            <p className="mt-4 text-base leading-relaxed text-slate-600">
              Standar CE dan ISO 13485 di seluruh tipe; nomor AKD tersedia pada tipe tertentu. Minimum pemesanan masker dikonfirmasi admin sesuai tipe.
            </p>
          </div>
          <a href="/masker-comet" className="inline-flex min-h-11 flex-shrink-0 items-center gap-2 text-sm font-black text-sky-800 hover:underline">
            Bandingkan tipe di halaman masker COMET <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="h-3.5 w-3.5" />
          </a>
        </div>
        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {masks.map((mask, index) => <MaskCard key={mask.sku} mask={mask} index={index} />)}
        </div>
      </div>
    </section>
  )
}
